import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { VideoAttachment } from 'apollo-gql'
import cn from 'classnames'

import VideoNotReady from 'components/VideoNotReady/VideoNotReady'
import VideoPlayerWithControls from 'components/VideoPlayerWithControls/VideoPlayerWithControls'
import useResponsive from 'hooks/useResponsive'

import styles from './MemmoPlayer.module.css'

type MemmoPlayerProps = {
  video: VideoAttachment
  autoPlay?: boolean
  rounded?: boolean
  onPlay: () => void
  children: React.ReactNode
  handleOnVideoEnd?: () => void
  handleOnVideoIsPlaying?: () => void
  handleOnVideoIsPaused?: () => void
  text: string
  customPlayIconStyles?: string
  customReplayIconStyles?: string
}

function MemmoPlayer(
  {
    video,
    autoPlay = true,
    rounded = true,
    onPlay: onPlayCallback,
    children = undefined,
    handleOnVideoEnd = () => undefined,
    handleOnVideoIsPlaying = () => undefined,
    handleOnVideoIsPaused = () => undefined,
    text = undefined,
    customPlayIconStyles,
    customReplayIconStyles,
  }: MemmoPlayerProps,
  ref: React.Ref<{ [key: string]: () => void } | undefined>,
) {
  const { isMobile } = useResponsive()
  const [isVideoEnded, setIsVideoEnded] = useState(false)
  const [isPlaying, setIsPlaying] = useState(autoPlay)
  const initialRender = useRef(true)

  const { url, thumbnailUrl, width, height, ready, available } = video || {}

  useEffect(() => {
    if (isPlaying) {
      initialRender.current = false
    }
  }, [isPlaying])

  const handlePlay = useCallback(() => {
    setIsPlaying(true)
    handleOnVideoIsPlaying()
    if (initialRender.current) {
      onPlayCallback()
    }
  }, [onPlayCallback, handleOnVideoIsPlaying])

  const horizontalVideo = width / height > 1
  const videoContainerStyle =
    isMobile && horizontalVideo
      ? {
          paddingBottom: `${height / (width / 100)}%`,
        }
      : {}

  return (
    <div
      className={cn(styles.base, {
        [styles.baseRounded]: rounded,
      })}
    >
      <div className={styles.videoContainer} style={videoContainerStyle}>
        <div className={styles.videoAspect}>
          {!ready && <VideoNotReady />}
          {!available && (
            <VideoNotReady thumbnailUrl={thumbnailUrl}>
              {children}
            </VideoNotReady>
          )}
          {ready && available && (
            <VideoPlayerWithControls
              ref={ref}
              src={url}
              horizontalVideo={horizontalVideo}
              width={width}
              height={height}
              poster={thumbnailUrl}
              onPlay={handlePlay}
              onPause={() => {
                setIsPlaying(false)
                handleOnVideoIsPaused()
              }}
              showFullscreenControl
              showVolumeControl
              playButtonSize={62}
              fullScreenOnPlay
              onVideoEnd={() => {
                setIsVideoEnded(true)
                handleOnVideoEnd()
              }}
              isVideoEnded={isVideoEnded}
              text={text}
              customPlayIconStyles={customPlayIconStyles}
              customReplayIconStyles={customReplayIconStyles}
            >
              {children}
            </VideoPlayerWithControls>
          )}
        </div>
      </div>
    </div>
  )
}

export default forwardRef(MemmoPlayer)
